import { getPersonFullName } from '../../helpers'
import { authorizedFetch } from '../../helpers/authorizedFetch'
import trans from '../../helpers/trans'
import { generateApiUrl } from '../../helpers/url'
import getIncluded from '../../japi/getIncluded'
import getMultipleIncluded from '../../japi/getMultipleIncluded'
import isResourceDocument from '../../japi/guards/isResourceDocument'
import { JapiDocument } from '../../japi/types/Document'
import { CommentResource } from '../Comment/Comment'
import { transformToComment } from '../Comment/CommentTransformers'
import { DepartmentResource } from '../Department/Department'
import { transformToDepartment } from '../Department/DepartmentTransformers'
import { DistrictDivisionDistrictResource } from '../DistrictDivisionDistrict/DistrictDivisionDistrict'
import { FetchResult, FetchResultType } from '../FetchResult'
import { UserResource } from '../User/User'
import { transformToBasicUser } from '../User/UserTransformers'
import {
    AddDistrictDivisionDistrictPlanningFormData,
    DistrictDivisionDistrictPlanning,
    DistrictDivisionDistrictPlanningResource,
    EditDistrictDivisionDistrictPlanningFormData,
} from './DistrictDivisionDistrictPlanning'
import {
    transformAddDistrictDivisionPlanningFormDataToRequest,
    transformEditDistrictDivisionPlanningOrderFormDataToRequest,
    transformToDistrictDivisionDistrictPlanning,
} from './DistrictDivisionDistrictPlanningTransformers'

export const postDistrictDivisionDistrictPlanningApiCall = async (formData: AddDistrictDivisionDistrictPlanningFormData): Promise<FetchResult<DistrictDivisionDistrictPlanning, string>> => {
    const apiData = transformAddDistrictDivisionPlanningFormDataToRequest(formData)

    try {
        const includes = [
            'department',
            'user',
            'districtDivisionDistrict',
        ]

        const url = generateApiUrl({
            endpoint: '/district-division-district-plannings',
            queryParams: {
                include: includes.join(','),
            },
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify({ data: apiData }),
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<DistrictDivisionDistrictPlanningResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const userResource = getIncluded(doc, doc.data, 'user') as UserResource
        const departmentResource = getIncluded(doc, doc.data, 'department') as DepartmentResource
        const districtDivisionDistrictResource = getIncluded(doc, doc.data, 'districtDivisionDistrict') as DistrictDivisionDistrictResource

        const user = transformToBasicUser(userResource)
        const department = transformToDepartment(departmentResource)
        const districtDivisionDistrictPlanning = transformToDistrictDivisionDistrictPlanning(
            doc.data,
            department,
            getPersonFullName(user.person),
            user.id,
            districtDivisionDistrictResource.id,
        )

        return {
            status: 200,
            type: FetchResultType.Success,
            data: districtDivisionDistrictPlanning,
        }
    } catch (error) {
        console.error('[postDistrictDivisionDistrictPlanningApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const deleteDistrictDivisionDistrictPlanningApiCall = async (districtDivisionDistrictPlanningId: string): Promise<FetchResult<boolean, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/district-division-district-plannings/${districtDivisionDistrictPlanningId}`,
        })

        const response = await authorizedFetch(url, {
            method: 'DELETE',
        })

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: true,
        }
    } catch (error) {
        console.error('[deleteDistrictDivisionDistrictPlanningApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const editDistrictDivisionDistrictPlanningOrderApiCall = async (formData: EditDistrictDivisionDistrictPlanningFormData): Promise<FetchResult<DistrictDivisionDistrictPlanning, string>> => {
    const apiData = transformEditDistrictDivisionPlanningOrderFormDataToRequest(formData)

    try {
        const includes = [
            'department',
            'user',
            'districtDivisionDistrict',
            'comments',
            'comments.owner',
        ]

        const url = generateApiUrl({
            endpoint: `/district-division-district-plannings/${formData.planningId}`,
            queryParams: {
                include: includes.join(','),
            },
        })

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify({ data: apiData }),
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<DistrictDivisionDistrictPlanningResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const userResource = getIncluded(doc, doc.data, 'user') as UserResource
        const departmentResource = getIncluded(doc, doc.data, 'department') as DepartmentResource
        const districtDivisionDistrictResource = getIncluded(doc, doc.data, 'districtDivisionDistrict') as DepartmentResource
        const commentsResource = (getMultipleIncluded(doc, doc.data, 'comments') || []) as CommentResource[]

        const comments = commentsResource.map(commentResource => {
            const commentOwnerResource = getIncluded(doc, commentResource, 'owner') as UserResource

            return transformToComment(
                commentResource,
                commentOwnerResource,
            )
        })

        const user = transformToBasicUser(userResource)
        const department = transformToDepartment(departmentResource)
        const districtDivisionDistrictPlanning = transformToDistrictDivisionDistrictPlanning(
            doc.data,
            department,
            getPersonFullName(user.person),
            user.id,
            districtDivisionDistrictResource.id,
            comments,
        )

        return {
            status: 200,
            type: FetchResultType.Success,
            data: districtDivisionDistrictPlanning,
        }
    } catch (error) {
        console.error('[editDistrictDivisionDistrictPlanningOrderApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
